// CB:BEGIN admin-alerts-page
import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

interface ProjectAlert {
  id: string;
  alertType: string;
  severity: string;
  message: string;
  projectId: string;
  projectAddress?: string;
  projectStatus?: string;
  createdAt: string;
}

interface StalledEscrow {
  id: string;
  projectId: string;
  projectAddress?: string;
  status: string;
  totalAmountCents: number;
  stalledSince: string;
}

interface PendingInvite {
  id: string;
  email: string;
  role: string;
  createdAt: string;
  expiresAt: string;
}

interface NoBidAlert {
  projectId: string;
  projectAddress?: string;
  status: string;
  statusEnteredAt?: string;
}

interface ExhaustedNotification {
  id: string;
  type: string;
  channel: string;
  recipientType: string;
  errorMessage: string | null;
  retryCount: number;
  updatedAt: string;
  recipientUserId: string;
}

interface PmAnomaly {
  projectId: string;
  projectAddress?: string;
  issue: string;
}

interface AlertsData {
  projectAlerts: ProjectAlert[];
  stalledEscrows: StalledEscrow[];
  pendingInvites: PendingInvite[];
  failedNotifications: { count: number; exhaustedCount: number; recentExhausted: ExhaustedNotification[] };
  noBidAlerts: NoBidAlert[];
  pmAnomalies: PmAnomaly[];
  summary: {
    totalProjectAlerts: number;
    totalStalledEscrows: number;
    totalPendingInvites: number;
    totalFailedNotifications: number;
    totalExhaustedNotifications: number;
    totalNoBidProjects: number;
    totalPmAnomalies: number;
  };
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-300',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  LOW: 'bg-blue-100 text-blue-800 border-blue-300',
};

interface Props {
  onNavigate?: (screen: string) => void;
}

const AdminAlertsPage: React.FC<Props> = ({ onNavigate }) => {
  const { getToken } = useAuth();
  const [data, setData] = useState<AlertsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => { fetchAlerts(); }, []);

  async function fetchAlerts() {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/admin/alerts', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function resolveAlert(id: string) {
    setResolving(id);
    try {
      const token = await getToken();
      await fetch(`/api/admin/alerts/${id}/resolve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      await fetchAlerts();
    } finally {
      setResolving(null);
    }
  }

  async function runScan() {
    setScanning(true);
    try {
      const token = await getToken();
      await fetch('/api/admin/alerts/run-stuck-scan', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      await fetchAlerts();
    } finally {
      setScanning(false);
    }
  }

  function fmt(iso: string) { return new Date(iso).toLocaleString(); }
  function fmtDollars(cents: number) { return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` ; }

  if (loading) return <div className="p-6 text-gray-500">Loading alerts...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;
  if (!data) return null;

  const { summary } = data;
  const totalIssues = summary.totalProjectAlerts + summary.totalStalledEscrows + summary.totalNoBidProjects + summary.totalFailedNotifications + summary.totalPmAnomalies;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Alerts</h1>
          <p className="text-sm text-gray-500 mt-1">{totalIssues} items need attention</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runScan}
            disabled={scanning}
            className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-40"
          >
            {scanning ? 'Scanning...' : 'Run Stuck Scan'}
          </button>
          <button onClick={fetchAlerts} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200">
            Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Project Alerts', value: summary.totalProjectAlerts, color: 'red' },
          { label: 'Stalled Escrows', value: summary.totalStalledEscrows, color: 'orange' },
          { label: 'No-Bid Projects', value: summary.totalNoBidProjects, color: 'yellow' },
          { label: 'Pending Invites', value: summary.totalPendingInvites, color: 'blue' },
          { label: 'Failed Notifs', value: summary.totalFailedNotifications, color: 'purple' },
          { label: 'Exhausted Notifs', value: summary.totalExhaustedNotifications, color: 'purple' },
          { label: 'PM Anomalies', value: summary.totalPmAnomalies, color: 'red' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border rounded-lg p-3 text-center shadow-sm">
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Project Alerts */}
      {data.projectAlerts.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Project Alerts ({data.projectAlerts.length})</h2>
          <div className="space-y-2">
            {data.projectAlerts.map((a) => (
              <div key={a.id} className={`border rounded-lg p-3 flex items-start justify-between ${SEVERITY_COLORS[a.severity] ?? 'bg-gray-50 border-gray-200'}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase">{a.severity}</span>
                    <span className="text-xs text-gray-600">·</span>
                    <span className="text-xs font-mono">{a.alertType}</span>
                  </div>
                  <p className="text-sm">{a.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{fmt(a.createdAt)}</p>
                </div>
                <button
                  onClick={() => resolveAlert(a.id)}
                  disabled={resolving === a.id}
                  className="ml-3 text-xs px-2 py-1 bg-white rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 shrink-0"
                >
                  Resolve
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* No-Bid Projects */}
      {data.noBidAlerts.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Projects with No Bids ({data.noBidAlerts.length})</h2>
          <div className="space-y-2">
            {data.noBidAlerts.map((p) => (
              <div key={p.projectId} className="border rounded-lg p-3 bg-yellow-50 border-yellow-200 flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium">{p.projectAddress ?? p.projectId}</p>
                  <p className="text-xs text-gray-500">{p.status} · entered {p.statusEnteredAt ? fmt(p.statusEnteredAt) : '—'}</p>
                </div>
                {onNavigate && (
                  <button
                    onClick={() => onNavigate(`admin-project-edit-${p.projectId}`)}
                    className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50"
                  >
                    View
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Stalled Escrows */}
      {data.stalledEscrows.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Stalled Escrows ({data.stalledEscrows.length})</h2>
          <div className="space-y-2">
            {data.stalledEscrows.map((e) => (
              <div key={e.id} className="border rounded-lg p-3 bg-orange-50 border-orange-200 flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium">{e.projectAddress ?? e.projectId}</p>
                  <p className="text-xs text-gray-500">{fmtDollars(e.totalAmountCents)} · stalled since {fmt(e.stalledSince)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Pending PM Invites */}
      {data.pendingInvites.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Pending PM Invites 48h+ ({data.pendingInvites.length})</h2>
          <div className="space-y-2">
            {data.pendingInvites.map((inv) => (
              <div key={inv.id} className="border rounded-lg p-3 bg-blue-50 border-blue-200">
                <p className="text-sm font-medium">{inv.email}</p>
                <p className="text-xs text-gray-500">Role: {inv.role} · Sent {fmt(inv.createdAt)} · Expires {fmt(inv.expiresAt)}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Failed / Exhausted Notifications */}
      {summary.totalFailedNotifications > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Failed Notifications</h2>
          <div className="border rounded-lg p-4 bg-purple-50 border-purple-200 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm">
                {summary.totalFailedNotifications} in FAILED state
                {summary.totalExhaustedNotifications > 0 && (
                  <span className="ml-2 text-red-700 font-semibold">({summary.totalExhaustedNotifications} permanently exhausted)</span>
                )}
              </p>
              <button
                onClick={() => onNavigate?.('admin-support-requests')}
                className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50"
              >
                View Logs
              </button>
            </div>
            {data.failedNotifications.recentExhausted.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-semibold text-gray-600">Permanently exhausted (last 10):</p>
                {data.failedNotifications.recentExhausted.map((n) => (
                  <div key={n.id} className="text-xs bg-white border border-purple-200 rounded p-2 flex justify-between">
                    <span className="font-mono">{n.type} / {n.channel}</span>
                    <span className="text-gray-500">{n.retryCount} retries · {fmt(n.updatedAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* PM Anomalies */}
      {data.pmAnomalies.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-800 mb-3">PM Anomalies ({data.pmAnomalies.length})</h2>
          <div className="space-y-2">
            {data.pmAnomalies.map((a) => (
              <div key={a.projectId} className="border rounded-lg p-3 bg-red-50 border-red-200 flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium">{a.projectAddress ?? a.projectId}</p>
                  <p className="text-xs text-gray-500">{a.issue}</p>
                </div>
                {onNavigate && (
                  <button
                    onClick={() => onNavigate(`admin-project-edit-${a.projectId}`)}
                    className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50"
                  >
                    View
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {totalIssues === 0 && (
        <div className="text-center py-12 text-gray-400 border rounded-lg bg-white">
          No active alerts. System is healthy.
        </div>
      )}
    </div>
  );
};

export default AdminAlertsPage;
// CB:END admin-alerts-page
